import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, ArrowLeft, Lock, Eye, Server, FileText, Globe, Clock, Users, AlertTriangle, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../constants';

export const PrivacyPolicyPage: React.FC = () => {
    const { t } = useLanguage();

    return (
        <div className="bg-[#0B1120] text-white min-h-screen pt-32 pb-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                <div className="mb-12">
                    <Link to={ROUTES.HOME} className="flex items-center text-gray-400 hover:text-white transition-colors group">
                        <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
                        {t('common.back')}
                    </Link>
                </div>

                <div>
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-blue-500/10 mb-8 ring-1 ring-blue-500/20">
                            <Shield size={40} className="text-blue-500" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black mb-6">Privacy Policy</h1>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            {t('privacyPolicy.subtitle')}
                        </p>
                    </div>

                    <div className="space-y-8 text-gray-300 leading-relaxed">
                        {/* Last Updated */}
                        <div className="bg-surface-dark border border-gray-800 rounded-2xl p-6 text-sm text-gray-400">
                            <strong>{t('privacyPolicy.lastUpdated')}</strong> {t('privacyPolicy.lastUpdatedDate')}
                        </div>

                        {/* 1. Data Controller */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-blue-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-blue-500/10 rounded-lg shrink-0">
                                    <Lock size={24} className="text-blue-500" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.s1.title')}</h2>
                                    <p className="mb-4">
                                        {t('privacyPolicy.s1.desc')}
                                    </p>
                                    <div className="bg-[#0B1120] rounded-xl p-4 space-y-2 text-sm">
                                        <p><strong className="text-white">{t('privacyPolicy.s1.company')}:</strong> ChartTower Corp. OÜ</p>
                                        <p><strong className="text-white">{t('privacyPolicy.s1.address')}:</strong> Ruunaoja 3, 11415 Tallinn, Estonia</p>
                                        <p><strong className="text-white">{t('privacyPolicy.s1.regNum')}:</strong> 17288413</p>
                                        <p><strong className="text-white">LEI Code:</strong> 9845001C375D5E411F72</p>
                                        <p><strong className="text-white">Email:</strong> <a href="mailto:info@ctc-corp.eu" className="text-blue-400 hover:underline">info@ctc-corp.eu</a></p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 2. DPO */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-blue-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-green-500/10 rounded-lg shrink-0">
                                    <Users size={24} className="text-green-500" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.s2.title')}</h2>
                                    <p className="mb-4">
                                        {t('privacyPolicy.s2.desc')}
                                    </p>
                                    <div className="bg-[#0B1120] rounded-xl p-4 space-y-2 text-sm">
                                        <p><strong className="text-white">{t('privacyPolicy.s2.dpo')}:</strong> Luca Marcialli</p>
                                        <p><strong className="text-white">Email:</strong> <a href="mailto:info@ctc-corp.eu" className="text-blue-400 hover:underline">info@ctc-corp.eu</a></p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 3. Data Collected */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-blue-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-blue-500/10 rounded-lg shrink-0">
                                    <Eye size={24} className="text-blue-500" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.s3.title')}</h2>
                                    <p className="mb-4">{t('privacyPolicy.s3.intro')}</p>

                                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">{t('privacyPolicy.s3.navTitle')}</h3>
                                    <p className="mb-2">
                                        {t('privacyPolicy.s3.navDesc')}
                                    </p>
                                    <ul className="list-disc pl-5 space-y-1 text-sm">
                                        {t('privacyPolicy.s3.navItems').map((item: string, i: number) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>

                                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">{t('privacyPolicy.s3.chatTitle')}</h3>
                                    <p className="mb-2">
                                        {t('privacyPolicy.s3.chatDesc')}
                                    </p>
                                    <ul className="list-disc pl-5 space-y-1 text-sm">
                                        {t('privacyPolicy.s3.chatItems').map((item: string, i: number) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mt-4">
                                        <p className="text-orange-400 text-sm flex items-start gap-2">
                                            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                                            <span><strong>{t('privacyPolicy.s3.chatWarning')}</strong> {t('privacyPolicy.s3.chatWarningDesc')}</span>
                                        </p>
                                    </div>

                                    <h3 className="text-lg font-semibold text-white mt-6 mb-3">{t('privacyPolicy.s3.cookiesTitle')}</h3>
                                    <p>
                                        {t('privacyPolicy.s3.cookiesDesc')} <Link to={ROUTES.COOKIE} className="text-blue-400 hover:underline">Cookie Policy</Link>).
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* 4. Purposes */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-blue-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-blue-500/10 rounded-lg shrink-0">
                                    <Server size={24} className="text-blue-500" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.s4.title')}</h2>
                                    <p className="mb-4">{t('privacyPolicy.s4.intro')}</p>

                                    <div className="space-y-4">
                                        <div className="bg-[#0B1120] rounded-xl p-4">
                                            <h4 className="text-white font-semibold mb-2">{t('privacyPolicy.s4.p1Title')}</h4>
                                            <p className="text-sm">{t('privacyPolicy.s4.p1Desc')}</p>
                                            <p className="text-xs text-gray-500 mt-2">{t('privacyPolicy.s4.p1Legal')}</p>
                                        </div>

                                        <div className="bg-[#0B1120] rounded-xl p-4">
                                            <h4 className="text-white font-semibold mb-2">{t('privacyPolicy.s4.p2Title')}</h4>
                                            <p className="text-sm">{t('privacyPolicy.s4.p2Desc')}</p>
                                            <p className="text-xs text-gray-500 mt-2">{t('privacyPolicy.s4.p2Legal')}</p>
                                        </div>

                                        <div className="bg-[#0B1120] rounded-xl p-4">
                                            <h4 className="text-white font-semibold mb-2">{t('privacyPolicy.s4.p3Title')}</h4>
                                            <p className="text-sm">{t('privacyPolicy.s4.p3Desc')}</p>
                                            <p className="text-xs text-gray-500 mt-2">{t('privacyPolicy.s4.p3Legal')}</p>
                                        </div>

                                        <div className="bg-[#0B1120] rounded-xl p-4">
                                            <h4 className="text-white font-semibold mb-2">{t('privacyPolicy.s4.p4Title')}</h4>
                                            <p className="text-sm">{t('privacyPolicy.s4.p4Desc')}</p>
                                            <p className="text-xs text-gray-500 mt-2">{t('privacyPolicy.s4.p4Legal')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 5. Recipients */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-blue-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-purple-500/10 rounded-lg shrink-0">
                                    <Globe size={24} className="text-purple-500" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.s5.title')}</h2>
                                    <p className="mb-4">{t('privacyPolicy.s5.intro')}</p>

                                    <div className="space-y-4">
                                        <div className="bg-[#0B1120] rounded-xl p-4">
                                            <h4 className="text-white font-semibold mb-2">{t('privacyPolicy.s5.h1Title')}</h4>
                                            <p className="text-sm"><strong>Cloudflare Inc.</strong> - {t('privacyPolicy.s5.h1Desc')}</p>
                                            <p className="text-xs text-gray-500 mt-1">{t('privacyPolicy.s5.h1Note')}</p>
                                        </div>

                                        <div className="bg-[#0B1120] rounded-xl p-4">
                                            <h4 className="text-white font-semibold mb-2">{t('privacyPolicy.s5.h2Title')}</h4>
                                            <p className="text-sm">{t('privacyPolicy.s5.h2Desc')}</p>
                                        </div>
                                    </div>

                                    <p className="mt-4 text-sm">
                                        <strong className="text-green-400">{t('privacyPolicy.s5.noteLabel')}</strong> {t('privacyPolicy.s5.note')}
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* 6. Data Retention */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-blue-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-orange-500/10 rounded-lg shrink-0">
                                    <Clock size={24} className="text-orange-500" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.s6.title')}</h2>
                                    <p className="mb-4">{t('privacyPolicy.s6.intro')}</p>

                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <span className="text-blue-400 font-mono text-sm">•</span>
                                            <span><strong className="text-white">{t('privacyPolicy.s6.i1Label')}</strong> {t('privacyPolicy.s6.i1Desc')}</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="text-blue-400 font-mono text-sm">•</span>
                                            <span><strong className="text-white">{t('privacyPolicy.s6.i2Label')}</strong> {t('privacyPolicy.s6.i2Desc')}</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="text-blue-400 font-mono text-sm">•</span>
                                            <span><strong className="text-white">{t('privacyPolicy.s6.i3Label')}</strong> {t('privacyPolicy.s6.i3Desc')}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* 7. Rights */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-blue-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-blue-500/10 rounded-lg shrink-0">
                                    <FileText size={24} className="text-blue-500" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.s7.title')}</h2>
                                    <p className="mb-4">{t('privacyPolicy.s7.intro')}</p>
                                    <ul className="list-disc pl-5 space-y-2">
                                        <li><strong className="text-white">{t('privacyPolicy.s7.r1Label')}</strong> - {t('privacyPolicy.s7.r1Desc')}</li>
                                        <li><strong className="text-white">{t('privacyPolicy.s7.r2Label')}</strong> - {t('privacyPolicy.s7.r2Desc')}</li>
                                        <li><strong className="text-white">{t('privacyPolicy.s7.r3Label')}</strong> - {t('privacyPolicy.s7.r3Desc')}</li>
                                        <li><strong className="text-white">{t('privacyPolicy.s7.r4Label')}</strong> - {t('privacyPolicy.s7.r4Desc')}</li>
                                        <li><strong className="text-white">{t('privacyPolicy.s7.r5Label')}</strong> - {t('privacyPolicy.s7.r5Desc')}</li>
                                        <li><strong className="text-white">{t('privacyPolicy.s7.r6Label')}</strong> - {t('privacyPolicy.s7.r6Desc')}</li>
                                        <li><strong className="text-white">{t('privacyPolicy.s7.r7Label')}</strong> - {t('privacyPolicy.s7.r7Desc')}</li>
                                    </ul>

                                    <div className="bg-[#0B1120] rounded-xl p-4 mt-6">
                                        <p className="text-sm">
                                            <strong className="text-white">{t('privacyPolicy.s7.exercise')}</strong><br />
                                            {t('common.contactEmail')}: <a href="mailto:info@ctc-corp.eu" className="text-blue-400 hover:underline">info@ctc-corp.eu</a>
                                        </p>
                                    </div>

                                    <div className="bg-[#0B1120] rounded-xl p-4 mt-4">
                                        <p className="text-sm">
                                            <strong className="text-white">{t('privacyPolicy.s7.complaint')}</strong><br />
                                            {t('privacyPolicy.s7.complaintBody')}<br />
                                            <strong>{t('privacyPolicy.s7.complaintAuthority')}</strong><br />
                                            <a href="https://www.aki.ee" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">www.aki.ee</a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 8. Security */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-blue-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-green-500/10 rounded-lg shrink-0">
                                    <Shield size={24} className="text-green-500" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.s8.title')}</h2>
                                    <p className="mb-4">{t('privacyPolicy.s8.intro')}</p>
                                    <ul className="list-disc pl-5 space-y-2">
                                        {t('privacyPolicy.s8.items').map((item: string, i: number) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* 9. Changes */}
                        <section className="bg-surface-dark border border-gray-800 rounded-3xl p-8 hover:border-blue-500/30 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-gray-500/10 rounded-lg shrink-0">
                                    <FileText size={24} className="text-gray-400" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.s9.title')}</h2>
                                    <p>
                                        {t('privacyPolicy.s9.desc')}
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Contact */}
                        <section className="bg-gradient-to-r from-blue-900/20 to-transparent border border-blue-500/30 rounded-3xl p-8">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-blue-500/20 rounded-lg shrink-0">
                                    <Mail size={24} className="text-blue-400" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white mb-4">{t('privacyPolicy.contact.title')}</h2>
                                    <p className="mb-4">{t('privacyPolicy.contact.desc')}</p>
                                    <div className="space-y-2 text-sm">
                                        <p><strong className="text-white">{t('privacyPolicy.contact.email')}:</strong> <a href="mailto:info@ctc-corp.eu" className="text-blue-400 hover:underline">info@ctc-corp.eu</a></p>
                                        <p><strong className="text-white">{t('privacyPolicy.contact.dpo')}:</strong> Luca Marcialli</p>
                                        <p><strong className="text-white">{t('privacyPolicy.contact.address')}:</strong> Ruunaoja 3, 11415 Tallinn, Estonia</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                    </div>
                </div>
            </div>
        </div>
    );
};
